import axiosInstance from "@/api/axiosInstance";
import { AxiosError } from "axios";
import { ENDPOINTS, JWT_KEY } from "@/constants/api";
import { requestHandler } from "@/helpers/requestHandler";
import { User } from "@/common/model/User";

interface OtpVerifyResponse {
  success: boolean;
  message: string;
  accesstoken: string;
  userId: number;
  email: string;
  isadmin: boolean;
  competitor: boolean;
  isspotmoderator: boolean;
  freesubscription: boolean;
  ispro_server: boolean;
  subscriptionTier: string;
}

export const AuthService = {
  sendCode: async (email: string): Promise<{ success: boolean; message: string }> => {
    return requestHandler(async () => {
      try {
        const response = await axiosInstance.post(ENDPOINTS.AUTH.SEND_CODE, {
          email,
        });
        return { success: true, message: response.data?.message || "Code sent" };
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          if (error.response?.status === 404) {
            throw new Error("Account not found");
          }
          if (error.response?.status === 400) {
            throw new Error(error.response.data?.message || "Invalid request");
          }
        }
        throw error;
      }
    }, "AuthService -> sendCode");
  },

  verifyCode: async (email: string, code: string): Promise<User> => {
    return requestHandler(async () => {
      try {
        const response = await axiosInstance.post(ENDPOINTS.AUTH.VERIFY_CODE, {
          email,
          code,
          device: {
            brand: "Web",
            model: "Browser",
            resolution: window.screen.width + "x" + window.screen.height,
            accesstoken: null,
            uniqueid: "web-" + Math.random().toString(36).substring(7),
            id: null,
            watchcode: null,
          },
        });

        const data: OtpVerifyResponse = response.data;

        if (!data.success || !data.accesstoken) {
          throw new Error(data.message || "Verification failed");
        }

        const user: User = {
          id: data.userId,
          accesstoken: data.accesstoken,
          username: data.email,
          name: data.email.split("@")[0],
          gender: "",
          isadmin: data.isadmin,
          maxairtime: 0,
          maxdistance: 0,
          maxheight: 0,
          ownprofile: true,
          privateaccount: false,
        };

        return user;
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          if (error.response?.status === 400) {
            throw new Error(error.response.data?.message || "Invalid code");
          }
        }
        throw error;
      }
    }, "AuthService -> verifyCode");
  },

  resendCode: async (email: string): Promise<{ success: boolean; message: string }> => {
    return requestHandler(async () => {
      try {
        const response = await axiosInstance.post(ENDPOINTS.AUTH.RESEND_CODE, {
          email,
        });
        return { success: true, message: response.data?.message || "Code resent" };
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          if (error.response?.status === 400) {
            throw new Error(error.response.data?.message || "Too soon to resend");
          }
        }
        throw error;
      }
    }, "AuthService -> resendCode");
  },

  saveToken: (user: User | null) => {
    if (user) {
      localStorage.setItem(JWT_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(JWT_KEY);
    }
  },
};
